import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MagnifyingGlass, UploadSimple, Database, FileCsv } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FIELDS = [
  ["chassis_number", "Chassis Number"],
  ["model", "Model"],
  ["interior_palette", "Interior Pallet"],
  ["exterior_colour", "Exterior Colour"],
  ["decal_colour", "Decal Colour"],
  ["battery_configuration", "Battery Configuration"],
  ["solar_configuration", "Solar Configuration (incl. controllers)"],
  ["inverter_charger", "Inverter / Charger"],
  ["management_system", "Management System"],
  ["additional_options", "Additional Options"],
  ["axle", "Axle (Single / Dual)"],
  ["hub_type_id", "Hubs"],
  ["water_tank_capacity", "Plumbing Configuration"],
  ["hitch", "Hitch (e.g. DO35 / DO45)"],
  ["customer_name", "Customer (optional)"],
];
const BLANK = { ...Object.fromEntries(FIELDS.map(([k]) => [k, ""])), hub_source: "" };

function HubSelect({ editing, setEditing, hubs }) {
  const byCustomer = editing.hub_type_id && editing.hub_source === "customer";
  return (
    <div className="col-span-2">
      <Field label="Hubs" hint={hubs.length ? "Sets the bearing and seal numbers customers and Nick see. If left blank, the customer can pick their hubs on My Caravan." : "Add hub types on the Hub Types page first."}>
        <select value={editing.hub_type_id || ""} onChange={(e) => setEditing({ ...editing, hub_type_id: e.target.value, hub_source: e.target.value ? "admin" : "" })} className={inputCls} data-testid="caravan-field-hub_type_id">
          <option value="">— Not set —</option>
          {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
      </Field>
      {byCustomer && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-200" data-testid="caravan-hub-customer-note">
          Chosen by the customer, not yet confirmed.
          <button type="button" onClick={() => setEditing({ ...editing, hub_source: "admin" })} data-testid="caravan-hub-confirm" className="font-semibold text-[#73bf44] hover:underline">Confirm</button>
        </div>
      )}
    </div>
  );
}

export default function CaravanDatabase() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(() => searchParams.get("search") || "");
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [original, setOriginal] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [hubs, setHubs] = useState([]);
  const hubName = Object.fromEntries(hubs.map((h) => [h.id, h.name]));

  useEffect(() => {
    adminApi.get("/admin/hub-types").then(({ data }) => setHubs(data)).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/caravans", { params: q ? { search: q } : {} })
      .then(({ data }) => { setRows(data); setSelected(new Set()); })
      .catch((e) => toast.error(getErrorMessage(e, "Couldn't load caravans. Check your connection and retry.")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.chassis_number));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.chassis_number)));
  const toggleOne = (ch) =>
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(ch)) s.delete(ch); else s.add(ch);
      return s;
    });

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} selected caravan(s)? This cannot be undone.`)) return;
    try {
      const { data } = await adminApi.post("/admin/caravans/bulk-delete", { chassis_numbers: [...selected] });
      toast.success(`Deleted ${data.deleted} caravan(s)`);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const deleteAll = async () => {
    if (!window.confirm("Delete ALL caravans in the database? This cannot be undone — use it before re-uploading a corrected CSV.")) return;
    try {
      const { data } = await adminApi.delete("/admin/caravans");
      toast.success(`Deleted all ${data.deleted} caravan(s)`);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const openNew = () => { setEditing({ ...BLANK }); setIsNew(true); setEditOpen(true); };
  const openEdit = (r) => { setEditing({ ...BLANK, ...r }); setOriginal(r); setIsNew(false); setEditOpen(true); };

  const save = async () => {
    if (!editing.chassis_number) { toast.error("Chassis number is required"); return; }
    const payload = { ...editing, specifications: editing.specifications || {} };
    const hubUnchanged = !isNew && (editing.hub_type_id || "") === (original?.hub_type_id || "") && (editing.hub_source || "") === (original?.hub_source || "");
    if (hubUnchanged) { delete payload.hub_type_id; delete payload.hub_source; }
    try {
      if (isNew) await adminApi.post("/admin/caravans", payload);
      else await adminApi.put(`/admin/caravans/${encodeURIComponent(editing.chassis_number)}`, payload);
      toast.success("Saved"); setEditOpen(false); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.chassis_number}?`)) return;
    await adminApi.delete(`/admin/caravans/${encodeURIComponent(r.chassis_number)}`);
    toast.success("Deleted"); load();
  };

  const doImport = async () => {
    if (!csvFile) { toast.error("Choose a CSV file"); return; }
    const fd = new FormData();
    fd.append("file", csvFile);
    setImporting(true); setImportResult(null);
    try {
      const { data } = await adminApi.post("/admin/caravans/import", fd);
      setImportResult(data);
      toast.success(`Imported ${data.imported}, updated ${data.updated}`);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <AdminHeader
        title="Caravan Database"
        subtitle="Chassis numbers and equipment configurations Nick uses for caravan-specific advice"
        actions={
          <>
            {selected.size > 0 && (
              <button onClick={deleteSelected} data-testid="caravan-delete-selected" className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 rounded-full px-4 py-2.5 text-sm hover:bg-red-500/10 transition-colors">
                <Trash size={16} /> Delete Selected ({selected.size})
              </button>
            )}
            <button onClick={deleteAll} data-testid="caravan-delete-all" className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 rounded-full px-4 py-2.5 text-sm hover:bg-red-500/10 transition-colors">
              <Trash size={16} /> Delete All
            </button>
            <GhostButton onClick={() => { setImportResult(null); setCsvFile(null); setImportOpen(true); }} data-testid="caravan-import-button"><FileCsv size={16} /> Import CSV</GhostButton>
            <PrimaryButton onClick={openNew} data-testid="caravan-new"><Plus size={16} /> Add Caravan</PrimaryButton>
          </>
        }
      />
      <div className="p-8">
        <div className="flex items-center gap-2 bg-[#0F0F0F] border border-neutral-800 rounded-full px-4 py-2.5 w-80 mb-6 focus-within:border-[#73bf44] transition-colors">
          <MagnifyingGlass size={16} className="text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chassis, model, customer..." data-testid="caravan-search" className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600" />
        </div>

        {loading ? <div className="text-neutral-600">Loading...</div> : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3">
            <Database size={36} className="text-neutral-700" weight="duotone" /> No caravans. Import a CSV or add one manually.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[1120px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="pl-5 pr-2 py-3 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="caravan-select-all" className="accent-[#73bf44] w-4 h-4 cursor-pointer" />
                  </th>
                  <th className="px-5 py-3 font-medium">Chassis</th>
                  <th className="px-5 py-3 font-medium">Model</th>
                  <th className="px-5 py-3 font-medium">Exterior</th>
                  <th className="px-5 py-3 font-medium">Battery</th>
                  <th className="px-5 py-3 font-medium">Solar</th>
                  <th className="px-5 py-3 font-medium">Management</th>
                  <th className="px-5 py-3 font-medium">Axle</th>
                  <th className="px-5 py-3 font-medium">Hubs</th>
                  <th className="px-5 py-3 font-medium">Plumbing</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.chassis_number} data-testid="caravan-row" className={`border-b border-neutral-800/60 last:border-0 hover:bg-white/[0.02] ${selected.has(r.chassis_number) ? "bg-[#73bf44]/[0.05]" : ""}`}>
                    <td className="pl-5 pr-2 py-3.5">
                      <input type="checkbox" checked={selected.has(r.chassis_number)} onChange={() => toggleOne(r.chassis_number)} data-testid="caravan-select-row" className="accent-[#73bf44] w-4 h-4 cursor-pointer" />
                    </td>
                    <td className="px-5 py-3.5 font-mono text-[#73bf44] whitespace-nowrap">
                      {r.chassis_number}
                      {r.source === "production_sync" && (
                        <span title="Added from the production schedule" data-testid="caravan-from-production" className="ml-2 align-middle font-body text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border border-sky-500/30 text-sky-300 bg-sky-500/10">Prod</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-neutral-200">{r.model || "—"}</td>
                    <td className="px-5 py-3.5 text-neutral-400">{r.exterior_colour || "—"}</td>
                    <td className="px-5 py-3.5 text-neutral-400 max-w-[220px] truncate">{r.battery_configuration || "—"}</td>
                    <td className="px-5 py-3.5 text-neutral-400 max-w-[220px] truncate">{r.solar_configuration || "—"}</td>
                    <td className="px-5 py-3.5 text-neutral-400 max-w-[220px] truncate">{r.management_system || "—"}</td>
                    <td className="px-5 py-3.5 text-neutral-400" data-testid="caravan-axle-cell">{r.axle || "—"}</td>
                    <td className="px-5 py-3.5 text-neutral-400 max-w-[180px]" data-testid="caravan-hub-cell">
                      <span className="block truncate">{hubName[r.hub_type_id] || "—"}</span>
                      {r.hub_type_id && r.hub_source === "customer" && <span className="text-[0.6rem] font-semibold uppercase tracking-wider text-amber-300">Customer</span>}
                    </td>
                    <td className="px-5 py-3.5 text-neutral-400 max-w-[160px] truncate" data-testid="caravan-water-cell">{r.water_tank_capacity || "—"}</td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} data-testid="caravan-edit" className="inline-flex p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><PencilSimple size={16} /></button>
                      <button onClick={() => del(r)} data-testid="caravan-delete" className="inline-flex p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit / Add */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">{isNew ? "Add" : "Edit"} Caravan</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([key, label]) => key === "hub_type_id" ? (
                <HubSelect key={key} editing={editing} setEditing={setEditing} hubs={hubs} />
              ) : (
                <Field key={key} label={label}>
                  {key === "axle" ? (
                    <select value={editing.axle || ""} onChange={(e) => setEditing({ ...editing, axle: e.target.value })} className={inputCls} data-testid="caravan-field-axle">
                      <option value="">— Not set —</option>
                      <option value="Single">Single</option>
                      <option value="Dual">Dual</option>
                      {editing.axle && !["Single", "Dual"].includes(editing.axle) && <option value={editing.axle}>{editing.axle}</option>}
                    </select>
                  ) : (
                  <input
                    value={editing[key] || ""}
                    onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                    disabled={key === "chassis_number" && !isNew}
                    className={`${inputCls} ${key === "chassis_number" ? "font-mono" : ""} ${key === "chassis_number" && !isNew ? "opacity-60" : ""}`}
                    data-testid={`caravan-field-${key}`}
                  />
                  )}
                </Field>
              ))}
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <GhostButton onClick={() => setEditOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={save} data-testid="caravan-save">Save</PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import CSV */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-white">Import Caravans (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-neutral-500">
              Columns are auto-mapped. A <span className="text-[#73bf44]">chassis number</span> column is required. Existing chassis numbers are updated; new ones are added. Unmapped columns are kept as extra specifications.
            </p>
            <button onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-neutral-700 rounded-lg py-6 flex flex-col items-center gap-2 text-neutral-400 hover:border-[#73bf44] transition-colors" data-testid="caravan-csv-picker">
              <UploadSimple size={22} />
              <span className="text-sm">{csvFile ? csvFile.name : "Choose a CSV file"}</span>
            </button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />

            {importResult && (
              <div className="bg-black/40 border border-neutral-800 rounded-lg p-3 text-sm" data-testid="import-result">
                <div className="text-[#73bf44]">Imported: {importResult.imported} · Updated: {importResult.updated} · Skipped: {importResult.skipped}</div>
                {importResult.errors?.length > 0 && (
                  <ul className="text-xs text-amber-400 mt-2 list-disc pl-4 max-h-24 overflow-y-auto">
                    {importResult.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <GhostButton onClick={() => setImportOpen(false)}>Close</GhostButton>
              <PrimaryButton onClick={doImport} disabled={importing} data-testid="caravan-import-submit">{importing ? "Importing..." : "Import"}</PrimaryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
